import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {V2RequestsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {getFeatureStatus} from 'auth/feature-enablement/FeatureStatus';
import {isInaccessibleInfo} from 'auth/RequestAccess';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import notifications from 'notifications';
import preferences from 'Preferences';
import {
  RecentlyUsedRequestV2DefinitionsQuery,
  RecentlyUsedRequestV2DefinitionsQueryVariables,
  RecentlyUsedRequestV2DefinitionsQuery_requestV2Definitions as RequestV2DefinitionOrInaccessible,
  RecentlyUsedRequestV2DefinitionsQuery_requestV2Definitions_RequestV2DefinitionNode as RequestV2DefinitionGql,
} from 'processes/requests_v2/dashboard/__generated/RecentlyUsedRequestV2DefinitionsQuery';
import CreateRequestV2SubmissionModal from 'processes/requests_v2/modals/CreateRequestV2SubmissionModal';
import {FONT_PROXIMA_14, FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import {multiLineEllipsisText} from 'styles/Utilities';
import getTestProps from 'test/lib/cypress/getTestProps';
import Label from 'ui-library/bs/Label';
import {GRAY_LIGHT, GRAY_LIGHTER, GRAY_SEMIDARK, WHITE} from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import legacyRunModal from 'ui-library/legacyRunModal';
import {ellipsize} from 'util/NameUtil';

interface RequestV2DefinitionCardProps {
  requestDefinition: RequestV2DefinitionGql;
}

export function RequestV2DefinitionCard(props: RequestV2DefinitionCardProps): JSX.Element {
  return (
    <div
      className={css(styles.requestDefinitionCard)}
      {...getTestProps({
        component: 'RecentlyUsedRequestV2DefinitionCard',
        element: 'card',
        key: props.requestDefinition.name,
      })}
    >
      <div
        className={css(styles.requestDefinitionCardText)}
        onClick={() => {
          const featureStatus = getFeatureStatus({feature: V2RequestsFeature.V2_REQUESTS_CREATE});
          if (featureStatus.enabled) {
            legacyRunModal(CreateRequestV2SubmissionModal, {
              actionOrigin: ActionOrigin.REQUEST_V2_SIDEPANEL,
              initialRequestV2DefinitionId: props.requestDefinition.id,
            });
          } else if (featureStatus.userMessage != null) {
            notifications.warn(featureStatus.userMessage);
          }
        }}
      >
        <span className={css(styles.requestDefinitionTitle)}>{props.requestDefinition.name}</span>
        <span className={css(styles.requestDefinitionDescription)}>
          {ellipsize(props.requestDefinition.description ?? '', 200)}
        </span>
      </div>
    </div>
  );
}

interface RecentlyUsedRequestV2DefinitionsCardViewProps {
  requestDefinitionsOrInaccessible: Array<RequestV2DefinitionOrInaccessible>;
}

function RecentlyUsedRequestV2DefinitionsCardView(
  props: RecentlyUsedRequestV2DefinitionsCardViewProps
): JSX.Element {
  const requestDefinitions = props.requestDefinitionsOrInaccessible
    .filter(
      (requestV2DefinitionOrInaccessible): requestV2DefinitionOrInaccessible is RequestV2DefinitionGql =>
        !isInaccessibleInfo(requestV2DefinitionOrInaccessible) &&
        !requestV2DefinitionOrInaccessible.archiveData.archived
    )
    .slice(0, BenchlingConfig.REQUESTS_V2_MAX_NUMBER_OF_RECENTLY_USED_REQUEST_DEFINITIONS_TO_DISPLAY);

  return (
    <div className={css(styles.requestDefinitionCardRow)}>
      {requestDefinitions.length === 0 && (
        <span className={css(styles.requestDefinitionDescription)}>
          There are no recently used request types to show here.
        </span>
      )}
      {requestDefinitions.map((requestDefinition: RequestV2DefinitionGql) => (
        <RequestV2DefinitionCard requestDefinition={requestDefinition} key={requestDefinition.id} />
      ))}
    </div>
  );
}

export const RECENTLY_USED_REQUEST_V2_DEFINITIONS_QUERY = gql`
  query RecentlyUsedRequestV2DefinitionsQuery($requestV2DefinitionIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    requestV2Definitions(ids: $requestV2DefinitionIds) {
      ... on RequestV2DefinitionNode {
        id
        name
        description
        archiveData {
          archived
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function QueryingRecentlyUsedRequestV2DefinitionsCardView(): JSX.Element | null {
  const recentlyUsedRequestIds = preferences.get('RECENTLY_USED_REQUEST_V2_DEFINITIONS') ?? [];
  const result = useBenchlingQuery<
    RecentlyUsedRequestV2DefinitionsQuery,
    RecentlyUsedRequestV2DefinitionsQueryVariables
  >(RECENTLY_USED_REQUEST_V2_DEFINITIONS_QUERY, {
    variables: {requestV2DefinitionIds: recentlyUsedRequestIds},
    fetchPolicy: 'cache-and-network',
  });
  if (result.type === QueryResultType.ERROR) {
    notifications.error('Could not load request templates; try reloading the page.');
    return null;
  }
  return (
    <div>
      <Label className={css(styles.header)}>Recently Used</Label>
      {result.type === QueryResultType.PENDING && <CenteredHorizontalThrobber />}
      {result.type === QueryResultType.SUCCESS && (
        <RecentlyUsedRequestV2DefinitionsCardView
          requestDefinitionsOrInaccessible={result.data.requestV2Definitions}
        />
      )}
      <Label className={css(styles.header)}>All request types</Label>
    </div>
  );
}

const styles = StyleSheet.create({
  header: {
    padding: '16px 0px 0px 0px',
    marginBottom: 0,
  },
  requestDefinitionCard: {
    width: '25%',
    minWidth: 140,
    padding: 16,
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: 4,
    backgroundColor: WHITE,
    ':hover': {
      backgroundColor: GRAY_LIGHTER,
      cursor: 'pointer',
    },
  },
  noRecentlyUsedRequestsMessage: {
    color: GRAY_SEMIDARK,
  },
  requestDefinitionTitle: {
    ...FONT_PROXIMA_14,
    marginBottom: 4,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    ...multiLineEllipsisText(2),
  },
  requestDefinitionDescription: {
    color: GRAY_SEMIDARK,
    width: '100%',
    overflow: 'hidden',
    ...multiLineEllipsisText(2),
  },
  requestDefinitionCardRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: 16,
    paddingTop: 16,
  },
  requestDefinitionCardText: {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
});
